import { useEffect, useRef, useState } from "react";

function findScrollableParent(el) {
  let node = el;

  while (node && node !== document.body && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const overflowY = style.overflowY;
    const canScroll =
      (overflowY === "auto" || overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight;

    if (canScroll) return node;
    node = node.parentElement;
  }

  return null;
}

function getPageScrollTop() {
  return window.pageYOffset || document.documentElement.scrollTop || 0;
}

export default function useGlobalPullToRefresh({
  onRefresh,
  threshold = 84,
  maxPull = 132,
  resistance = 0.45,
  enabled = true,
} = {}) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const startYRef = useRef(0);
  const draggingRef = useRef(false);
  const lockedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const shouldIgnoreStart = (target) => {
      if (!target) return true;

      if (document.body.classList.contains("modal-open")) return true;
      if (target.closest?.("[data-ptr-ignore='true']")) return true;

      const scrollableParent = findScrollableParent(target);
      if (scrollableParent && scrollableParent.scrollTop > 0) return true;

      return false;
    };

    const onTouchStart = (e) => {
      if (isRefreshing) return;
      if (!e.touches || e.touches.length !== 1) return;

      const target = e.target;
      if (shouldIgnoreStart(target)) return;
      if (getPageScrollTop() > 0) return;

      startYRef.current = e.touches[0].clientY;
      draggingRef.current = true;
      lockedRef.current = false;
      setIsReady(false);
    };

    const onTouchMove = (e) => {
      if (!draggingRef.current || isRefreshing) return;
      if (!e.touches || e.touches.length !== 1) return;

      const currentY = e.touches[0].clientY;
      const delta = currentY - startYRef.current;

      if (delta <= 0) {
        setPullDistance(0);
        setIsPulling(false);
        setIsReady(false);
        return;
      }

      if (getPageScrollTop() > 0) {
        draggingRef.current = false;
        setPullDistance(0);
        setIsPulling(false);
        setIsReady(false);
        return;
      }

      const damped = Math.min(maxPull, delta * resistance);

      if (!lockedRef.current && delta > 6) {
        lockedRef.current = true;
      }

      if (lockedRef.current) {
        e.preventDefault();
      }

      setIsPulling(true);
      setPullDistance(damped);
      setIsReady(damped >= threshold);
    };

    const finishRefresh = async () => {
      try {
        setIsRefreshing(true);
        if (typeof onRefresh === "function") {
          await onRefresh();
        } else {
          window.location.reload();
        }
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
        setIsPulling(false);
        setIsReady(false);
      }
    };

    const onTouchEnd = async () => {
      if (!draggingRef.current) return;

      draggingRef.current = false;

      if (isReady && !isRefreshing) {
        setPullDistance(threshold * 0.72);
        setIsPulling(false);
        await finishRefresh();
        return;
      }

      setPullDistance(0);
      setIsPulling(false);
      setIsReady(false);
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd, { passive: true });
    window.addEventListener("touchcancel", onTouchEnd, { passive: true });

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [
    enabled,
    isReady,
    isRefreshing,
    maxPull,
    onRefresh,
    resistance,
    threshold,
  ]);

  return {
    pullDistance,
    isPulling,
    isReady,
    isRefreshing,
    visible: isPulling || isRefreshing || pullDistance > 0,
  };
}
